import React, { useMemo, useState } from "react";
import { Card, Col, Row, Spin, Statistic, Table, Tag, Tooltip, Empty, Segmented } from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DollarOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  PercentageOutlined,
  RiseOutlined,
} from "@ant-design/icons";
import ApexChart from "react-apexcharts";
import { COLORS } from "constants/ChartConstant";
import { formatPrice } from "../../../../functions/formatters";
import { CURRENCY } from "../../../../constants/DeliveryConstants";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useMarketingMetrics, METRIC_SEGMENTS } from "../../../../data/hooks/useMarketingMetrics";

const MetricCard = ({ title, value, suffix, prefix, change, tooltip, icon, color }) => {
  const hasChange = change !== null && change !== undefined;
  const isPositive = hasChange && change >= 0;

  return (
    <Card style={{ height: "100%" }} bodyStyle={{ padding: "20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 8 }}>
            <span style={{ fontSize: "13px", color: "#8c8c8c" }}>{title}</span>
            {tooltip && (
              <Tooltip title={tooltip}>
                <InfoCircleOutlined style={{ fontSize: "12px", color: "#bfbfbf" }} />
              </Tooltip>
            )}
          </div>
          <Statistic
            value={value || 0}
            prefix={prefix}
            suffix={suffix}
            valueStyle={{ fontSize: "24px", fontWeight: 600 }}
          />
          {hasChange && (
            <div style={{ marginTop: 8 }}>
              <Tag color={isPositive ? "success" : "error"} style={{ fontSize: "12px" }}>
                {isPositive ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                {` ${Math.abs(change).toFixed(1)}%`}
              </Tag>
              <span style={{ fontSize: "11px", color: "#8c8c8c" }}>vs vorige periode</span>
            </div>
          )}
        </div>
        {/*{icon && (*/}
        {/*  <div*/}
        {/*    style={{*/}
        {/*      width: 48,*/}
        {/*      height: 48,*/}
        {/*      borderRadius: 12,*/}
        {/*      backgroundColor: color || "#f0f5ff",*/}
        {/*      display: "flex",*/}
        {/*      alignItems: "center",*/}
        {/*      justifyContent: "center",*/}
        {/*      fontSize: "20px",*/}
        {/*      color: color ? "#fff" : "#1890ff",*/}
        {/*    }}*/}
        {/*  >*/}
        {/*    {icon}*/}
        {/*  </div>*/}
        {/*)}*/}
      </div>
    </Card>
  );
};

const CLVFormulaCard = ({ aov, frequency, lifespan, clv }) => {
  const { t } = useTranslation();

  return (
    <Card title={t("marketing_metrics.clv_formula")} style={{ marginTop: 16 }}>
      <div style={{ textAlign: "center", padding: "16px 0" }}>
        <div style={{ fontSize: "16px", marginBottom: 16, color: "#595959" }}>
          <strong>CLV</strong> = AOV × {t("marketing_metrics.purchase_frequency")} ×{" "}
          {t("marketing_metrics.customer_lifespan")}
        </div>
        <div style={{ fontSize: "20px", fontWeight: 600, color: "#1890ff" }}>
          {CURRENCY}
          {formatPrice(aov || 0)}
          {" × "}
          {(frequency || 0).toFixed(2)}
          {" × "}
          {((lifespan || 0) / 12).toFixed(2)} jaar
        </div>
        <div style={{ fontSize: "28px", fontWeight: 700, marginTop: 12, color: "#52c41a" }}>
          = {CURRENCY}
          {formatPrice(clv || 0)}
        </div>
      </div>
      <div
        style={{
          marginTop: 16,
          padding: "12px",
          backgroundColor: "#f6ffed",
          borderRadius: "4px",
          fontSize: "12px",
          color: "#595959",
        }}
      >
        <strong>{t("marketing_metrics.formula_explanation")}</strong>
      </div>
    </Card>
  );
};

const TrendChart = ({ trendData, metric, title, formatter, color }) => {
  const { t } = useTranslation();

  if (!trendData || !trendData.labels || trendData.labels.length < 2) {
    return (
      <Card title={title}>
        <Empty description={t("marketing_metrics.not_enough_data_for_trend")} />
      </Card>
    );
  }

  return (
    <Card title={title}>
      <ApexChart
        options={{
          colors: [color || COLORS[0]],
          stroke: { curve: "smooth", width: 3 },
          chart: { toolbar: { show: false }, zoom: { enabled: false } },
          xaxis: {
            categories: trendData.labels,
            labels: { rotate: -45, rotateAlways: trendData.labels.length > 6 },
          },
          yaxis: {
            labels: { formatter: formatter || ((value) => value.toFixed(1)) },
          },
          tooltip: {
            y: { formatter: formatter || ((value) => value.toFixed(2)) },
          },
        }}
        series={[{ name: title, data: trendData[metric] }]}
        type="line"
        height={280}
      />
    </Card>
  );
};

const CohortTable = ({ cohorts }) => {
  const { t } = useTranslation();

  const dataSource = useMemo(() => {
    if (!cohorts) return [];
    const twelveMonthsAgo = new Date();
    twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
    const cutoff = `${twelveMonthsAgo.getFullYear()}-${String(twelveMonthsAgo.getMonth() + 1).padStart(2, "0")}`;

    return Object.entries(cohorts)
      .filter(([month]) => month >= cutoff)
      .map(([month, data]) => ({
        key: month,
        month,
        customers: data.customers || 0,
        orders: data.orders || 0,
        revenue: data.revenue || 0,
        repeatRate: data.repeatRate || 0,
        aov: data.orders > 0 ? data.revenue / data.orders : 0,
      }))
      .sort((a, b) => b.month.localeCompare(a.month));
  }, [cohorts]);

  const columns = [
    {
      title: t("marketing_metrics.cohort_month"),
      dataIndex: "month",
      key: "month",
    },
    {
      title: t("marketing_metrics.customers"),
      dataIndex: "customers",
      key: "customers",
      sorter: (a, b) => a.customers - b.customers,
    },
    {
      title: t("marketing_metrics.orders"),
      dataIndex: "orders",
      key: "orders",
      sorter: (a, b) => a.orders - b.orders,
    },
    {
      title: t("marketing_metrics.revenue"),
      dataIndex: "revenue",
      key: "revenue",
      render: (val) => CURRENCY + formatPrice(val),
      sorter: (a, b) => a.revenue - b.revenue,
    },
    {
      title: "AOV",
      dataIndex: "aov",
      key: "aov",
      render: (val) => CURRENCY + formatPrice(val),
      sorter: (a, b) => a.aov - b.aov,
    },
    {
      title: t("marketing_metrics.repeat_rate"),
      dataIndex: "repeatRate",
      key: "repeatRate",
      render: (val) => <Tag color={val >= 50 ? "success" : val >= 30 ? "warning" : "error"}>{val.toFixed(1)}%</Tag>,
      sorter: (a, b) => a.repeatRate - b.repeatRate,
    },
  ];

  return (
    <Card title={t("marketing_metrics.cohort_analysis")} style={{ marginTop: 16 }}>
      <Table dataSource={dataSource} columns={columns} pagination={false} size="small" scroll={{ x: 600 }} />
    </Card>
  );
};

const SEGMENT_OPTIONS = [
  { label: "VCE Landelijk", value: METRIC_SEGMENTS.VCE_LANDELIJK },
  { label: "VCE Regionaal", value: METRIC_SEGMENTS.VCE_REGIONAAL },
  { label: "HVO Landelijk", value: METRIC_SEGMENTS.HVO_LANDELIJK },
];

const MarketingMetricsDashboard = () => {
  const { t } = useTranslation();
  const [segment, setSegment] = useState(METRIC_SEGMENTS.VCE_LANDELIJK);
  const { latestMetrics, changes, getTrendData, isLoading, error, refresh } = useMarketingMetrics({ segment });

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spin size="large" />
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <DashboardContainer>
        <Card>
          <Empty description={t("marketing_metrics.error_loading")} />
        </Card>
      </DashboardContainer>
    );
  }

  if (!latestMetrics) {
    return (
      <DashboardContainer>
        <Row gutter={16} className="mb-3">
          <Col xs={24}>
            <PageTitle>{t("marketing_metrics.title")}</PageTitle>
            <SubTitle>{t("marketing_metrics.subtitle")}</SubTitle>
          </Col>
        </Row>
        <Card>
          <Empty description={t("marketing_metrics.no_data")} />
        </Card>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      <Row gutter={16} className="mb-3">
        <Col xs={24} sm={18}>
          <PageTitle>{t("marketing_metrics.title")}</PageTitle>
          <SubTitle>{t("marketing_metrics.subtitle")}</SubTitle>
          {latestMetrics.date && (
            <div style={{ marginTop: 4, fontSize: "12px", color: "#8c8c8c" }}>
              {t("marketing_metrics.last_updated")}: {latestMetrics.date}
              {latestMetrics.periodMonths &&
                ` • ${t("marketing_metrics.period")}: ${latestMetrics.periodMonths} ${t("marketing_metrics.months")}`}
            </div>
          )}
        </Col>
        <Col xs={24} sm={6} style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
          <ReloadOutlined onClick={() => refresh()} style={{ fontSize: 18, cursor: "pointer", color: "#1890ff" }} />
        </Col>
      </Row>

      <Row gutter={16} className="mb-3">
        <Col xs={24}>
          <Segmented
            options={SEGMENT_OPTIONS}
            value={segment}
            onChange={setSegment}
            size="large"
          />
        </Col>
      </Row>

      {/* Primary KPIs */}
      <Row gutter={16}>
        <Col xs={24} sm={12} md={8} lg={4}>
          <MetricCard
            title="AOV"
            value={formatPrice(latestMetrics.aov || 0)}
            prefix={CURRENCY}
            change={changes.aov}
            tooltip={t("marketing_metrics.aov_tooltip")}
            icon={<ShoppingCartOutlined />}
            color="#1890ff"
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <MetricCard
            title={t("marketing_metrics.purchase_frequency")}
            value={(latestMetrics.purchaseFrequency || 0).toFixed(2)}
            suffix={`× / ${t("marketing_metrics.year")}`}
            change={changes.purchaseFrequency}
            tooltip={t("marketing_metrics.frequency_tooltip")}
            icon={<ReloadOutlined />}
            color="#722ed1"
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <MetricCard
            title={t("marketing_metrics.customer_lifespan")}
            value={(latestMetrics.customerLifespanMonths || 0).toFixed(1)}
            suffix={t("marketing_metrics.months")}
            change={changes.customerLifespanMonths}
            tooltip={t("marketing_metrics.lifespan_tooltip")}
            icon={<ClockCircleOutlined />}
            color="#13c2c2"
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <MetricCard
            title="CLV"
            value={formatPrice(latestMetrics.clv || 0)}
            prefix={CURRENCY}
            change={changes.clv}
            tooltip={t("marketing_metrics.clv_tooltip")}
            icon={<RiseOutlined />}
            color="#52c41a"
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <MetricCard
            title={t("marketing_metrics.repeat_purchase_rate")}
            value={(latestMetrics.repeatPurchaseRate || 0).toFixed(1)}
            suffix="%"
            change={changes.repeatPurchaseRate}
            tooltip={t("marketing_metrics.repeat_rate_tooltip")}
            icon={<PercentageOutlined />}
            color="#fa8c16"
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <MetricCard
            title={t("marketing_metrics.unique_customers")}
            value={latestMetrics.totalCustomers || 0}
            change={changes.totalCustomers}
            tooltip={t("marketing_metrics.customers_tooltip")}
            icon={<TeamOutlined />}
            color="#eb2f96"
          />
        </Col>
      </Row>

      {/* Secondary stats */}
      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col xs={24} sm={12} md={6}>
          <Card bodyStyle={{ padding: "16px" }}>
            <Statistic
              title={t("marketing_metrics.total_orders")}
              value={latestMetrics.totalOrders || 0}
              valueStyle={{ fontSize: "20px" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bodyStyle={{ padding: "16px" }}>
            <Statistic
              title={t("marketing_metrics.total_revenue")}
              value={latestMetrics.totalRevenue || 0}
              precision={2}
              prefix={CURRENCY}
              valueStyle={{ fontSize: "20px" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bodyStyle={{ padding: "16px" }}>
            <Statistic
              title={t("marketing_metrics.single_purchase_customers")}
              value={latestMetrics.customersWithOneOrder || 0}
              valueStyle={{ fontSize: "20px" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bodyStyle={{ padding: "16px" }}>
            <Statistic
              title={t("marketing_metrics.multi_purchase_customers")}
              value={latestMetrics.customersWithMultipleOrders || 0}
              valueStyle={{ fontSize: "20px" }}
            />
          </Card>
        </Col>
      </Row>

      {/* CLV Formula breakdown */}
      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <CLVFormulaCard
            aov={latestMetrics.aov}
            frequency={latestMetrics.purchaseFrequency}
            lifespan={latestMetrics.customerLifespanMonths}
            clv={latestMetrics.clv}
          />
        </Col>
        <Col xs={24} lg={12}>
          <TrendChart
            trendData={getTrendData}
            metric="clv"
            title={t("marketing_metrics.clv_trend")}
            formatter={(value) => CURRENCY + formatPrice(value)}
            color={COLORS[1]}
          />
        </Col>
      </Row>

      {/* Trend charts */}
      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <TrendChart
            trendData={getTrendData}
            metric="aov"
            title={t("marketing_metrics.aov_trend")}
            formatter={(value) => CURRENCY + formatPrice(value)}
            color={COLORS[0]}
          />
        </Col>
        <Col xs={24} lg={12}>
          <TrendChart
            trendData={getTrendData}
            metric="repeatRate"
            title={t("marketing_metrics.repeat_rate_trend")}
            formatter={(value) => value.toFixed(1) + "%"}
            color={COLORS[4]}
          />
        </Col>
      </Row>

      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <TrendChart
            trendData={getTrendData}
            metric="frequency"
            title={t("marketing_metrics.frequency_trend")}
            formatter={(value) => value.toFixed(2) + "×"}
            color={COLORS[2]}
          />
        </Col>
        <Col xs={24} lg={12}>
          <TrendChart
            trendData={getTrendData}
            metric="lifespan"
            title={t("marketing_metrics.lifespan_trend")}
            formatter={(value) => value.toFixed(1) + " mnd"}
            color={COLORS[5]}
          />
        </Col>
      </Row>

      {/* Cohort analysis */}
      {latestMetrics.cohorts && (
        <Row gutter={16} style={{ marginTop: 16 }}>
          <Col xs={24}>
            <CohortTable cohorts={latestMetrics.cohorts} />
          </Col>
        </Row>
      )}

      {/* Info about data scope */}
      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col xs={24}>
          <Card style={{ backgroundColor: "#f6f8fa" }}>
            <div style={{ fontSize: "12px", color: "#595959" }}>
              <strong>{t("marketing_metrics.data_note_title")}</strong>
              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                <li>{t("marketing_metrics.data_note_landelijk")}</li>
                <li>{t("marketing_metrics.data_note_period")}</li>
                <li>{t("marketing_metrics.data_note_email")}</li>
              </ul>
            </div>
          </Card>
        </Col>
      </Row>
    </DashboardContainer>
  );
};

const DashboardContainer = styled.div`
  padding: 1rem;
  background-color: #f9fafb;
  min-height: 100vh;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
`;

const PageTitle = styled.h1`
  font-size: 20px;
  margin-bottom: 0;
`;

const SubTitle = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 0;
  margin-top: 4px;
`;

export default MarketingMetricsDashboard;
